// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/Banners.jsx  —  Quickry
//  Copy to: C:\quickry\admin\src\pages\Banners.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import { api } from "../utils/api";
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Image as ImageIcon, Link, Palette, Type, AlignLeft,
  X, Save, Eye, EyeOff, GripVertical, ExternalLink,
} from "lucide-react";

const GRADIENTS = [
  { label: "Fire (Orange)",  value: "linear-gradient(135deg,#7f1d1d 0%,#c2410c 40%,#f97316 100%)" },
  { label: "Forest (Green)", value: "linear-gradient(135deg,#052e16 0%,#166534 40%,#22c55e 100%)" },
  { label: "Ocean (Blue)",   value: "linear-gradient(135deg,#0c1445 0%,#1d4ed8 40%,#60a5fa 100%)" },
  { label: "Violet",         value: "linear-gradient(135deg,#2e1065 0%,#6d28d9 40%,#a78bfa 100%)" },
  { label: "Rose",           value: "linear-gradient(135deg,#4c0519 0%,#be123c 40%,#fb7185 100%)" },
  { label: "Teal",           value: "linear-gradient(135deg,#042f2e 0%,#0f766e 40%,#2dd4bf 100%)" },
  { label: "Sunset",         value: "linear-gradient(135deg,#713f12 0%,#d97706 40%,#fbbf24 100%)" },
  { label: "Night",          value: "linear-gradient(135deg,#0f0f0f 0%,#1e293b 40%,#334155 100%)" },
];

const ROUTES = [
  "/restaurants", "/grocery", "/courier", "/medicine",
  "/offers", "/profile", "/orders",
];

const EMPTY = {
  title: "", subtitle: "", badge: "", btnText: "Order Now",
  route: "/restaurants", image: "", gradient: GRADIENTS[0].value,
  sortOrder: 0, isActive: true,
};

export default function Banners() {
  const [banners,  setBanners]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);   // banner being edited
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [useImage, setUseImage] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.banners.getAll();
      setBanners(res.banners || []);
    } catch { setError("Failed to load banners"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setUseImage(false);
    setError("");
    setShowForm(true);
  };

  const openEdit = (b) => {
    setEditing(b._id);
    setForm({
      title:     b.title     || "",
      subtitle:  b.subtitle  || "",
      badge:     b.badge     || "",
      btnText:   b.btnText   || "Order Now",
      route:     b.route     || "/restaurants",
      image:     b.image     || "",
      gradient:  b.gradient  || GRADIENTS[0].value,
      sortOrder: b.sortOrder || 0,
      isActive:  b.isActive  !== false,
    });
    setUseImage(!!b.image);
    setError("");
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!useImage) payload.image = "";
      if (editing) {
        await api.banners.update(editing, payload);
      } else {
        await api.banners.create(payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try {
      await api.banners.toggle(id);
      load();
    } catch {}
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.banners.delete(id);
      load();
    } catch {}
  };

  const T = {
    primary:"#f97316", light:"#fff7ed", mid:"#fed7aa", dark:"#ea580c",
    white:"#ffffff", bg:"#f8fafc", text:"#0f172a", sub:"#64748b",
    border:"#e2e8f0", green:"#16a34a", red:"#ef4444",
  };

  const inp = {
    width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10,
    padding:"10px 12px", fontSize:"0.875rem", background:T.bg,
    color:T.text, outline:"none", fontFamily:"inherit",
  };

  return (
    <div style={{ padding:"24px", fontFamily:"'Inter',system-ui,sans-serif", color:T.text, maxWidth:960, margin:"0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:"1.5rem", letterSpacing:"-0.5px" }}>Hero Banners</div>
          <div style={{ color:T.sub, fontSize:"0.82rem", marginTop:2 }}>Customer app ke home page pe dikhne wale banners manage karo</div>
        </div>
        <button onClick={openCreate}
          style={{ background:T.primary, color:"#fff", border:"none", borderRadius:10, padding:"10px 18px", fontWeight:700, fontSize:"0.875rem", cursor:"pointer", display:"flex", alignItems:"center", gap:7 }}>
          <Plus size={16}/> Add Banner
        </button>
      </div>

      {/* ── Banner List ── */}
      {loading ? (
        <div style={{ color:T.sub, padding:"40px 0", textAlign:"center" }}>Loading...</div>
      ) : banners.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 20px", background:T.white, borderRadius:16, border:`1.5px dashed ${T.border}` }}>
          <ImageIcon size={40} color={T.border} style={{ marginBottom:12 }}/>
          <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:6 }}>No banners yet</div>
          <div style={{ color:T.sub, fontSize:"0.82rem", marginBottom:18 }}>Add your first banner — it will appear on the customer home screen</div>
          <button onClick={openCreate}
            style={{ background:T.primary, color:"#fff", border:"none", borderRadius:10, padding:"10px 20px", fontWeight:700, cursor:"pointer" }}>
            Add First Banner
          </button>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {banners.map((b) => (
            <div key={b._id} style={{ background:T.white, borderRadius:16, border:`1.5px solid ${b.isActive ? T.border : "#fecaca"}`, overflow:"hidden", display:"flex", boxShadow:"0 1px 6px rgba(0,0,0,0.04)" }}>

              {/* Preview strip */}
              <div style={{ width:130, minHeight:90, background: b.image ? `url(${b.image}) center/cover no-repeat` : b.gradient, flexShrink:0, display:"flex", flexDirection:"column", justifyContent:"flex-end", padding:"10px 12px", position:"relative" }}>
                {b.image && <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.35)" }}/>}
                <div style={{ position:"relative", color:"#fff", fontWeight:800, fontSize:"0.9rem", lineHeight:1.2 }}>{b.title}</div>
                {b.subtitle && <div style={{ position:"relative", color:"rgba(255,255,255,0.8)", fontSize:"0.65rem", marginTop:2 }}>{b.subtitle}</div>}
              </div>

              {/* Info */}
              <div style={{ flex:1, padding:"14px 16px", minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:"0.95rem" }}>{b.title}</span>
                  <span style={{ background: b.isActive ? "#dcfce7" : "#fee2e2", color: b.isActive ? T.green : T.red, fontSize:"0.62rem", fontWeight:700, padding:"2px 8px", borderRadius:100 }}>
                    {b.isActive ? "ACTIVE" : "INACTIVE"}
                  </span>
                  {b.badge && (
                    <span style={{ background:"#fff7ed", color:T.dark, fontSize:"0.62rem", fontWeight:700, padding:"2px 8px", borderRadius:100, border:`1px solid ${T.mid}` }}>
                      {b.badge}
                    </span>
                  )}
                </div>
                {b.subtitle && <div style={{ color:T.sub, fontSize:"0.78rem", marginBottom:4 }}>{b.subtitle}</div>}
                <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:"0.72rem", color:T.sub }}>
                  <ExternalLink size={11}/> {b.route}
                  <span style={{ marginLeft:8 }}>· Order: {b.sortOrder}</span>
                  {b.image && <span style={{ marginLeft:4, color:T.primary }}>· Has image</span>}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"0 14px", flexShrink:0 }}>
                <button onClick={() => toggle(b._id)} title={b.isActive ? "Deactivate" : "Activate"}
                  style={{ background:"none", border:`1.5px solid ${T.border}`, borderRadius:9, padding:"7px 9px", cursor:"pointer", display:"flex", color: b.isActive ? T.green : T.sub }}>
                  {b.isActive ? <ToggleRight size={17}/> : <ToggleLeft size={17}/>}
                </button>
                <button onClick={() => openEdit(b)} title="Edit"
                  style={{ background:"none", border:`1.5px solid ${T.border}`, borderRadius:9, padding:"7px 9px", cursor:"pointer", display:"flex", color:T.primary }}>
                  <Pencil size={15}/>
                </button>
                <button onClick={() => deleteBanner(b._id)} title="Delete"
                  style={{ background:"none", border:`1.5px solid #fecaca`, borderRadius:9, padding:"7px 9px", cursor:"pointer", display:"flex", color:T.red }}>
                  <Trash2 size={15}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Form ── */}
      {showForm && (
        <div onClick={() => setShowForm(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.white, borderRadius:20, width:"100%", maxWidth:560, maxHeight:"92vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>

            {/* Modal Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 22px 16px", borderBottom:`1px solid ${T.border}` }}>
              <div style={{ fontWeight:800, fontSize:"1.1rem" }}>{editing ? "Edit Banner" : "New Banner"}</div>
              <button onClick={() => setShowForm(false)}
                style={{ background:T.bg, border:"none", borderRadius:9, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <X size={16} color={T.sub}/>
              </button>
            </div>

            <div style={{ padding:"20px 22px" }}>

              {error && (
                <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", marginBottom:16, color:T.red, fontSize:"0.82rem", fontWeight:600 }}>
                  {error}
                </div>
              )}

              {/* Live Preview */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:8 }}>Preview</div>
                <div style={{ background: useImage && form.image ? `url(${form.image}) center/cover no-repeat` : form.gradient, borderRadius:14, padding:"18px 16px", minHeight:100, position:"relative", overflow:"hidden" }}>
                  {useImage && form.image && <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.4)" }}/>}
                  <div style={{ position:"absolute", width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.06)", right:-40, top:-60 }}/>
                  {form.badge && <div style={{ display:"inline-block", background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:"0.58rem", fontWeight:800, padding:"3px 9px", borderRadius:100, marginBottom:8, position:"relative" }}>{form.badge}</div>}
                  <div style={{ fontWeight:900, fontSize:"1.5rem", color:"#fff", letterSpacing:"-1px", marginBottom:4, position:"relative" }}>{form.title || "Banner Title"}</div>
                  <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.8)", marginBottom:12, position:"relative" }}>{form.subtitle || "Banner subtitle"}</div>
                  <div style={{ background:"#fff", display:"inline-block", borderRadius:100, padding:"6px 14px", fontWeight:700, fontSize:"0.72rem", position:"relative" }}>{form.btnText || "Order Now"}</div>
                </div>
              </div>

              {/* Title */}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>
                  <Type size={11} style={{ marginRight:4 }}/>Title
                </label>
                <input style={inp} value={form.title} placeholder='e.g. "50% OFF"'
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}/>
              </div>

              {/* Subtitle */}
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>
                  <AlignLeft size={11} style={{ marginRight:4 }}/>Subtitle
                </label>
                <input style={inp} value={form.subtitle} placeholder='e.g. "On your first food order"'
                  onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}/>
              </div>

              {/* Badge + Button text */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                <div>
                  <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>Badge</label>
                  <input style={inp} value={form.badge} placeholder='e.g. "LIMITED TIME"'
                    onChange={e => setForm(f => ({ ...f, badge: e.target.value }))}/>
                </div>
                <div>
                  <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>Button Text</label>
                  <input style={inp} value={form.btnText} placeholder="Order Now"
                    onChange={e => setForm(f => ({ ...f, btnText: e.target.value }))}/>
                </div>
              </div>

              {/* Route + Sort */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 80px", gap:12, marginBottom:14 }}>
                <div>
                  <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>
                    <Link size={11} style={{ marginRight:4 }}/>Tap Goes To
                  </label>
                  <select style={{ ...inp }}
                    value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))}>
                    {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:6 }}>Order</label>
                  <input style={inp} type="number" value={form.sortOrder} min={0}
                    onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))}/>
                </div>
              </div>

              {/* Background — toggle image or gradient */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:"0.72rem", fontWeight:700, color:T.sub, textTransform:"uppercase", letterSpacing:"0.8px", display:"block", marginBottom:10 }}>
                  <Palette size={11} style={{ marginRight:4 }}/>Background
                </label>
                <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                  <button onClick={() => setUseImage(false)}
                    style={{ flex:1, padding:"8px", borderRadius:9, border:`1.5px solid ${!useImage ? T.primary : T.border}`, background:!useImage ? T.light : T.bg, color:!useImage ? T.primary : T.sub, fontWeight:700, fontSize:"0.78rem", cursor:"pointer" }}>
                    🎨 Gradient
                  </button>
                  <button onClick={() => setUseImage(true)}
                    style={{ flex:1, padding:"8px", borderRadius:9, border:`1.5px solid ${useImage ? T.primary : T.border}`, background:useImage ? T.light : T.bg, color:useImage ? T.primary : T.sub, fontWeight:700, fontSize:"0.78rem", cursor:"pointer" }}>
                    🖼️ Image URL
                  </button>
                </div>

                {!useImage ? (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {GRADIENTS.map(g => (
                      <button key={g.value} onClick={() => setForm(f => ({ ...f, gradient: g.value }))}
                        style={{ borderRadius:10, overflow:"hidden", border:`2px solid ${form.gradient === g.value ? T.primary : "transparent"}`, cursor:"pointer", padding:0 }}>
                        <div style={{ background:g.value, height:32 }}/>
                        <div style={{ fontSize:"0.68rem", fontWeight:600, color:T.sub, padding:"4px 6px", textAlign:"center" }}>{g.label}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <input style={inp} value={form.image} placeholder="https://example.com/banner.jpg"
                    onChange={e => setForm(f => ({ ...f, image: e.target.value }))}/>
                )}
              </div>

              {/* Active toggle */}
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20, padding:"12px 14px", background:T.bg, borderRadius:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:"0.85rem" }}>Active</div>
                  <div style={{ color:T.sub, fontSize:"0.72rem" }}>Banner customer app pe dikhega ya nahi</div>
                </div>
                <button onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                  style={{ background:"none", border:"none", cursor:"pointer", display:"flex", color: form.isActive ? T.green : T.sub }}>
                  {form.isActive ? <ToggleRight size={28}/> : <ToggleLeft size={28}/>}
                </button>
              </div>

              {/* Save */}
              <button onClick={save} disabled={saving}
                style={{ width:"100%", background:T.primary, color:"#fff", border:"none", borderRadius:12, padding:"13px", fontWeight:800, fontSize:"0.9rem", cursor:saving?"not-allowed":"pointer", opacity:saving?0.8:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <Save size={16}/> {saving ? "Saving..." : editing ? "Update Banner" : "Create Banner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}